import { computed, effect, signal } from "@preact/signals";

/**
 * How Obean feels (P5-UX-07, ADR 0047): how loud, whether phones buzz, less motion, larger text,
 * and the note after an hour of play (ADR 0043). Kept in this browser, like the sound switch
 * (sound-pref.ts, which stays the bar's mute): they belong to the device, since a phone and a
 * laptop want different ones.
 */
export interface Settings {
  volume: Volume;
  vibration: boolean;
  /** "phone": as the phone's own reduce motion; "reduce": always less motion. */
  motion: "phone" | "reduce";
  largerText: boolean;
  hourNote: boolean;
}

export type Volume = "quiet" | "normal" | "loud";

/** The game's master volume for each choice; "normal" is the quiet default (founder, 2026-10-06). */
export const VOLUMES: Record<Volume, number> = { quiet: 0.15, normal: 0.35, loud: 0.65 };

export const DEFAULTS: Settings = {
  volume: "normal",
  vibration: true,
  motion: "phone",
  largerText: false,
  hourNote: true,
};

const KEY = "obean.settings";

/** What's kept, cleaned: anything unknown or missing is the default. */
export function cleanSettings(raw: unknown): Settings {
  const o = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const bool = (k: "vibration" | "largerText" | "hourNote") => {
    const v = o[k];
    return typeof v === "boolean" ? v : DEFAULTS[k];
  };
  return {
    volume: o["volume"] === "quiet" || o["volume"] === "loud" ? o["volume"] : "normal",
    vibration: bool("vibration"),
    motion: o["motion"] === "reduce" ? "reduce" : "phone",
    largerText: bool("largerText"),
    hourNote: bool("hourNote"),
  };
}

function stored(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? cleanSettings(JSON.parse(raw)) : DEFAULTS;
  } catch {
    return DEFAULTS; // no storage (a private window), or something unreadable: the defaults
  }
}

export const settings = signal<Settings>(stored());

export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
  settings.value = { ...settings.value, [key]: value };
  try {
    localStorage.setItem(KEY, JSON.stringify(settings.value));
  } catch {
    // No storage: the setting lasts for this page only.
  }
}

const phoneReduces = signal(false);
if (typeof matchMedia === "function") {
  const query = matchMedia("(prefers-reduced-motion: reduce)");
  phoneReduces.value = query.matches;
  query.addEventListener("change", (e) => {
    phoneReduces.value = e.matches;
  });
}

/** Whether to move less: the phone says so, or the player chose it here. */
export const reduceMotion = computed(
  () => settings.value.motion === "reduce" || phoneReduces.value,
);

/** The game's volume, 0-1. */
export const volume = computed(() => VOLUMES[settings.value.volume]);

/** A buzz on phones that can, if the player allows it. */
export function vibrate(pattern: number | readonly number[]): void {
  if (!settings.peek().vibration) return;
  try {
    if ("vibrate" in navigator) navigator.vibrate(pattern as number | number[]);
  } catch {
    // No vibration here.
  }
}

/**
 * The page follows the settings: `data-motion="reduce"` stills the app's animations (styles.css),
 * `data-text="large"` sets its pages' text larger. Called once at start.
 */
export function applySettings(root: HTMLElement = document.documentElement): () => void {
  return effect(() => {
    if (reduceMotion.value) root.dataset["motion"] = "reduce";
    else delete root.dataset["motion"];
    if (settings.value.largerText) root.dataset["text"] = "large";
    else delete root.dataset["text"];
  });
}
